import React, { useState, useEffect } from "react";
import useAdminDataStore, { selectCurrentGroupData } from "../../../../store/adminData/adminData";
import { doc, getDoc } from "firebase/firestore";
import { Checkbox, Divider, Spin } from "antd";
import { db } from "../../../../services/firebaseService/connection";
import { chantApi } from "../../../../utils/chantApi";
import logger from "../../../../utils/logger";
import { toast } from "react-toastify";
import DescriptionText from "../../../../CommonComponents/DescriptionText";
import ChantButton from "../../../../CommonComponents/ChantButton/ChantButton";
import dayjs from "dayjs";

const TERMS = [
  {
    title: "Content",
    body: "[Club Name] can share news, events, polls, and sponsored giveaways directly to your group.",
  },
  {
    title: "Partners",
    body: "Official club partners and special offers may be displayed in your group's Home, Partners and Member Card sections.",
  },
  {
    title: "Member Benefits",
    body: "Your members will see club-specific content and rewards and may receive a digital club member card.",
  },
  {
    title: "Photo Sharing",
    body: "Members can optionally choose to share uploaded photos. Note: Sharing a photo grants the connection permission for commercial use.",
  },
];

const GUARDRAILS = [
  {
    title: "Limited Access",
    body: "The club cannot see your internal group content, including private news, events, polls, partners, or chants.",
  },
  {
    title: "Chat Privacy",
    body: "The club cannot see your group's private Chat Channels.",
  },
  {
    title: "Global Transparency",
    body: "Any posts made to the Global Chat Stream or Global Channels remain visible to the club and all connected groups.",
  },
  {
    title: "Member Opt-In",
    body: "Members must individually accept the connection to participate. Upon acceptance, the club is granted access to the member's Name, Email, and Basic Profile info.",
  },
];

const ConnectionSettings = () => {
  const groupData = useAdminDataStore(selectCurrentGroupData);

  const [connectionRequest, setConnectionRequest] = useState(null);
  const [connectionAccepted, setConnectionAccepted] = useState(null);
  const [loading, setLoading] = useState(true);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [accepting, setAccepting] = useState(false);

  const fetchConnectionData = async () => {
    if (!groupData?.id) return;
    setLoading(true);
    try {
      const groupSnap = await getDoc(doc(db, "group", groupData.id));
      if (groupSnap.exists()) {
        const data = groupSnap.data();
        setConnectionRequest(data.connectionRequest || null);
        setConnectionAccepted(data.connectionAccepted || null);
      }
    } catch (error) {
      logger.error("Error fetching connection data", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConnectionData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupData?.id]);

  const handleAccept = async () => {
    try {
      setAccepting(true);
      await chantApi.post(
        `${import.meta.env.VITE_GCP_BASE_URL}/group/${groupData.id}/connection/accept`,
        {}
      );
      toast.success("Connection accepted successfully.");
      await fetchConnectionData();
    } catch (error) {
      logger.error("Error accepting connection", error);
      toast.error("Failed to accept connection. Please try again.");
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: "24px", textAlign: "center" }}>
        <Spin />
      </div>
    );
  }

  if (!connectionRequest) return null;

  const parentName = connectionRequest.parentName || "Parent Organization";
  const groupName = groupData?.groupName || "your group";
  const isAccepted = connectionRequest.status === "accepted";

  return (
    <div style={{ maxWidth: "700px" }}>
      <DescriptionText showBackground>
        <p>
          Chant provides a way for clubs, leagues and other partner
          organizations to connect with your group and provide content and other
          benefits. Your group content remains private (you control
          content/group access). To learn more or have Chant establish a
          partnership with your club/league reach out to{" "}
          <a href="mailto:hello@chant.fan">hello@chant.fan</a>.
        </p>
      </DescriptionText>

      <div
        style={{
          marginTop: "var(--screen-general-margin)",
          backgroundColor: "var(--primary-white-background)",
          padding: "20px 24px",
        }}
      >
        {isAccepted ? (
          <AcceptedState
            connectionAccepted={connectionAccepted}
            parentName={parentName}
          />
        ) : (
          <PendingState
            parentName={parentName}
            groupName={groupName}
            termsAccepted={termsAccepted}
            setTermsAccepted={setTermsAccepted}
            accepting={accepting}
            onAccept={handleAccept}
          />
        )}
      </div>
    </div>
  );
};

const PendingState = ({
  parentName,
  groupName,
  termsAccepted,
  setTermsAccepted,
  accepting,
  onAccept,
}) => (
  <div>
    <div
      style={{
        padding: "16px",
        backgroundColor: "#f6ffed",
        border: "1px solid #b7eb8f",
        borderRadius: "4px",
        marginBottom: "20px",
      }}
    >
      <p style={{ margin: 0, fontSize: "15px" }}>
        <strong>{parentName}</strong> is inviting <strong>{groupName}</strong>{" "}
        to establish an official connection. Accepting this request bridges the
        gap between the organization and your community, unlocking exclusive
        content and rewards for your members while maintaining your group's
        independence.
      </p>
    </div>

    <Divider orientation="left" style={{ fontWeight: "bold" }}>
      What happens when you accept
    </Divider>

    <div style={{ marginBottom: "20px" }}>
      {TERMS.map((term) => (
        <div key={term.title} style={{ marginBottom: "12px" }}>
          <span style={{ fontWeight: "600" }}>{term.title}: </span>
          <span style={{ color: "#555" }}>
            {term.body.replace("[Club Name]", parentName)}
          </span>
        </div>
      ))}
    </div>

    <Divider orientation="left" style={{ fontWeight: "bold" }}>
      Data &amp; Privacy Guardrails
    </Divider>

    <div style={{ marginBottom: "24px" }}>
      {GUARDRAILS.map((item) => (
        <div key={item.title} style={{ marginBottom: "12px" }}>
          <span style={{ fontWeight: "600" }}>{item.title}: </span>
          <span style={{ color: "#555" }}>{item.body}</span>
        </div>
      ))}
    </div>

    <Divider />

    <div style={{ marginBottom: "20px" }}>
      <Checkbox
        checked={termsAccepted}
        onChange={(e) => setTermsAccepted(e.target.checked)}
      >
        I have read and accept the terms above on behalf of {"{groupName}".replace("{groupName}", "my group")}.
      </Checkbox>
    </div>

    <ChantButton
      onClick={onAccept}
      loading={accepting}
      disabled={!termsAccepted || accepting}
    >
      Accept Connection
    </ChantButton>
  </div>
);

const AcceptedState = ({ connectionAccepted, parentName }) => (
  <div>
    <div
      style={{
        padding: "16px",
        backgroundColor: "#f6ffed",
        border: "1px solid #b7eb8f",
        borderRadius: "4px",
        marginBottom: "16px",
      }}
    >
      <p style={{ margin: 0, fontSize: "15px" }}>
        ✓ Connected to <strong>{parentName}</strong>
      </p>
    </div>

    {connectionAccepted && (
      <p style={{ color: "#555", marginBottom: "8px" }}>
        Accepted by{" "}
        <strong>{connectionAccepted.adminName || "Admin"}</strong> on{" "}
        <strong>
          {connectionAccepted.acceptedOn
            ? dayjs(connectionAccepted.acceptedOn).format("MMMM D, YYYY")
            : "—"}
        </strong>
        .
      </p>
    )}

    <p style={{ color: "#888", fontSize: "13px" }}>
      To remove the connection for the group contact{" "}
      <a href="mailto:support@chant.fan">support@chant.fan</a>.
    </p>
  </div>
);

export default ConnectionSettings;
